import { Check, ChevronRight, Layers, RefreshCw, X } from 'lucide-react';
import { HierarchyMappingPreview, type HierarchyNode } from '../HierarchyMappingPreview';
import type { JiraConnection } from './useJiraConnection';

export function HierarchyPreviewModal({ jira }: { jira: JiraConnection }) {
  const {
    showHierarchyPreview,
    setShowHierarchyPreview,
    setShowExportJira,
    selectedJiraProject,
    hierarchyItems,
    setHierarchyItems,
    confirmJiraExport,
    isLoading,
  } = jira;

  return (
    <>
      {/* Hierarchy Preview Modal */}
      {showHierarchyPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--bg-raised)] shadow-xl max-w-4xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-[var(--rule)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[var(--slate-dim)] text-[var(--text-sec)]">
                  <Layers className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text-pri)]">Hierarchy Mapping Preview</h2>
                  <p className="text-sm text-[var(--text-ter)]">
                    Exporting to project: <strong>{selectedJiraProject}</strong>
                  </p>
                </div>
              </div>
              <button onClick={() => setShowHierarchyPreview(false)} className="p-2 hover:bg-[var(--hover-tint)]">
                <X className="h-5 w-5 text-[var(--text-ter)]" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <HierarchyMappingPreview
                nodes={hierarchyItems}
                direction="export"
                targetSystem="jira"
                onNodeTypeChange={(nodeId, newType) => {
                  const updateNodeType = (nodes: HierarchyNode[]): HierarchyNode[] => {
                    return nodes.map(node => {
                      if (node.id === nodeId) {
                        return { ...node, type: newType };
                      }
                      if (node.children) {
                        return { ...node, children: updateNodeType(node.children) };
                      }
                      return node;
                    });
                  };
                  setHierarchyItems(updateNodeType(hierarchyItems));
                }}
              />
            </div>

            <div className="p-4 border-t border-[var(--rule)] flex items-center justify-between bg-[var(--surface)]">
              <button
                onClick={() => setShowHierarchyPreview(false)}
                className="px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors flex items-center gap-2"
              >
                <ChevronRight className="h-4 w-4 rotate-180" />
                Back to Selection
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowHierarchyPreview(false);
                    setShowExportJira(false);
                  }}
                  className="px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmJiraExport}
                  disabled={isLoading}
                  className="px-4 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isLoading ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  Confirm & Export to Jira
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
